import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { FileField, required, TextInput, useInput, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  ExternalBankAccountType,
  PaymentMethodIntent,
  PaymentMethodType,
} from '@Common/types/bo/b2b/companies';
import {
  isSEPAZoneBic,
  ZodBankAccountIban,
  ZodUkAccountNumber,
  ZodUkSortCode,
} from '@Common/types/zod/bank_account';

import { FileInput, ShowSection } from '@sh4res/bo-common/components';
import { BicInput, IbanInput } from '@sh4res/bo-common/fields';

export const SIGNATURE_DELEGATION_VALUES = {
  SELF: 'self',
  EXTERNAL_SIGNATORY: 'external_signatory',
} as const;

const testId = 'BoExternalB2bCompanyPaymentMethodsPaymentDetailsStep';

const validateScanSortCode = (value?: string) => {
  if (!ZodUkSortCode.safeParse(value).success) {
    return 'resources.bo_common.b2b.companies.payment_methods.validation.scan_sort_code_invalid';
  }
  return undefined;
};

const validateScanAccountNumber = (value?: string) => {
  if (!ZodUkAccountNumber.safeParse(value).success) {
    return 'resources.bo_common.b2b.companies.payment_methods.validation.scan_account_number_invalid';
  }
  return undefined;
};

const validateIntlAccountNumber = (value?: string) => {
  if (ZodBankAccountIban.safeParse(value).success) {
    return 'resources.bo_common.b2b.companies.payment_methods.validation.intl_account_number_is_iban';
  }
  return undefined;
};

const validateIntlBic = (value?: string) => {
  if (value && isSEPAZoneBic(value)) {
    return 'resources.bo_common.b2b.companies.payment_methods.validation.intl_sepa_zone_not_allowed';
  }
  return undefined;
};

const getAccountNumberValidators = (
  requiredFields: boolean,
  accountType: ExternalBankAccountType,
) => {
  if (!requiredFields) {
    return [];
  }
  if (accountType === 'scan') {
    return [required(), validateScanAccountNumber];
  }
  if (accountType === 'intl') {
    return [required(), validateIntlAccountNumber];
  }
  return [required()];
};

const getBicValidators = (requiredFields: boolean, accountType: ExternalBankAccountType) => {
  if (!requiredFields) {
    return [];
  }
  if (accountType === 'intl') {
    return [required(), validateIntlBic];
  }
  return [required()];
};

const SignatureDelegationRadio = () => {
  const translate = useTranslate();
  const { field } = useInput({
    source: 'signature_delegation',
    defaultValue: SIGNATURE_DELEGATION_VALUES.SELF,
    validate: [required()],
    isRequired: true,
  });

  return (
    <>
      <Typography variant="body1" sx={{ mb: 1 }}>
        {translate(
          'resources.bo_common.b2b.companies.payment_methods.sections.details.signature_authorization.question',
        )}
      </Typography>
      <RadioGroup
        name={field.name}
        value={field.value ?? ''}
        onChange={(event) => field.onChange(event.target.value)}>
        <FormControlLabel
          value={SIGNATURE_DELEGATION_VALUES.SELF}
          control={<Radio data-testid={`${testId}_SelfSignatureRadio`} />}
          label={translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.signature_authorization.options.self',
          )}
        />
        <FormControlLabel
          value={SIGNATURE_DELEGATION_VALUES.EXTERNAL_SIGNATORY}
          control={<Radio data-testid={`${testId}_ExternalSignatoryRadio`} />}
          label={translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.signature_authorization.options.external_signatory',
          )}
        />
      </RadioGroup>
    </>
  );
};

export const PaymentDetailsStep = ({ companyName }: { companyName: string }) => {
  const translate = useTranslate();
  const paymentMethodType = useWatch<{ payment_method_type: PaymentMethodType }>({
    name: 'payment_method_type',
  });
  const paymentMethodIntent = useWatch<{ payment_method_intent?: PaymentMethodIntent }>({
    name: 'payment_method_intent',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PaymentMethodSection
        prefix=""
        companyName={companyName}
        requiredFields={true}
        paymentMethodType={paymentMethodType}
        paymentMethodIntent={paymentMethodIntent}
      />

      {paymentMethodType === 'sepa_direct_debit' && (
        <>
          <Typography variant="body2" sx={{ pt: 3, color: 'text.secondary' }}>
            {translate(
              'resources.bo_common.b2b.companies.payment_methods.sections.details.checkout_disclaimer',
            )}
          </Typography>

          <ShowSection
            leftAligned
            label="resources.bo_common.b2b.companies.payment_methods.sections.details.signature_authorization.title"
            noCard>
            <SignatureDelegationRadio />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {translate(
                'resources.bo_common.b2b.companies.payment_methods.sections.details.signature_authorization.hint',
              )}
            </Typography>
          </ShowSection>
        </>
      )}
    </Box>
  );
};

const ExternalBankAccountTypeRadio = ({ prefix }: { prefix: '' | 'invoicing_' }) => {
  const translate = useTranslate();
  const { setValue, unregister } = useFormContext();
  const accountType: ExternalBankAccountType = useWatch({
    name: `${prefix}account_type`,
    defaultValue: 'iban',
  });

  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {translate(
          'resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.account_type',
        )}
      </Typography>
      <RadioGroup
        row
        value={accountType}
        onChange={(event) => {
          setValue(`${prefix}account_type`, event.target.value);
          unregister([
            `${prefix}iban`,
            `${prefix}account_number`,
            `${prefix}sort_code`,
            `${prefix}bic`,
          ]);
        }}
        sx={{ mb: 2 }}>
        <FormControlLabel
          value="iban"
          control={<Radio data-testid={`${testId}_AccountTypeIbanRadio`} />}
          label={translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.account_type_iban',
          )}
        />
        <FormControlLabel
          value="intl"
          control={<Radio data-testid={`${testId}_AccountTypeIntlRadio`} />}
          label={translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.account_type_intl',
          )}
        />
        <FormControlLabel
          value="scan"
          control={<Radio data-testid={`${testId}_AccountTypeScanRadio`} />}
          label={translate(
            'resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.account_type_scan',
          )}
        />
      </RadioGroup>
    </>
  );
};

const ExternalBankAccountFields = ({
  prefix,
  accountType,
  requiredFields,
  ibanLabelKey,
  bicLabelKey,
}: {
  prefix: '' | 'invoicing_';
  accountType: ExternalBankAccountType;
  requiredFields: boolean;
  ibanLabelKey: string;
  bicLabelKey: string;
}) => (
  <>
    {accountType === 'iban' ? (
      <IbanInput
        source={`${prefix}iban`}
        label={ibanLabelKey}
        validate={requiredFields ? [required()] : []}
        testId={`${testId}_IbanInput`}
      />
    ) : null}

    {accountType === 'scan' ? (
      <TextInput
        source={`${prefix}sort_code`}
        label="resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.sort_code"
        validate={requiredFields ? [required(), validateScanSortCode] : []}
        data-testid={`${testId}_SortCodeInput`}
      />
    ) : null}

    {accountType === 'intl' || accountType === 'scan' ? (
      <TextInput
        source={`${prefix}account_number`}
        label="resources.bo_common.b2b.companies.payment_methods.sections.details.external_bank.account_number"
        validate={getAccountNumberValidators(requiredFields, accountType)}
        data-testid={`${testId}_AccountNumberInput`}
      />
    ) : null}

    <BicInput
      source={`${prefix}bic`}
      label={bicLabelKey}
      validate={getBicValidators(requiredFields, accountType)}
      testId={`${testId}_BicInput`}
    />
  </>
);

const PaymentMethodSection = ({
  prefix,
  companyName,
  requiredFields,
  paymentMethodType,
  paymentMethodIntent,
}: {
  prefix: '' | 'invoicing_';
  companyName: string;
  requiredFields: boolean;
  paymentMethodType: PaymentMethodType;
  paymentMethodIntent: PaymentMethodIntent | undefined;
}) => {
  const translate = useTranslate();
  const accountType: ExternalBankAccountType = useWatch({
    name: `${prefix}account_type`,
    defaultValue: 'iban',
  });

  const detailsLabel = `resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.${prefix ? 'invoicing_details' : 'details'}`;
  const descriptionKey = `resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.${paymentMethodIntent === 'ccb_withdrawal' ? 'ccb_withdrawal_description' : 'description'}`;
  const ibanLabelKey = `resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.iban`;
  const bicLabelKey = `resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.bic`;

  return (
    <>
      <ShowSection leftAligned label={detailsLabel} noCard>
        <Typography sx={{ mb: 2, ml: 0 }}>{translate(descriptionKey)}</Typography>

        {paymentMethodType === 'external_bank' && <ExternalBankAccountTypeRadio prefix={prefix} />}

        {paymentMethodType === 'sepa_direct_debit' ? (
          <>
            <IbanInput
              source={`${prefix}iban`}
              label={ibanLabelKey}
              validate={requiredFields ? [required()] : []}
              testId={`${testId}_IbanInput`}
            />
            <BicInput
              source={`${prefix}bic`}
              label={bicLabelKey}
              validate={requiredFields ? [required()] : []}
              testId={`${testId}_BicInput`}
            />
          </>
        ) : (
          <ExternalBankAccountFields
            prefix={prefix}
            accountType={accountType}
            requiredFields={requiredFields}
            ibanLabelKey={ibanLabelKey}
            bicLabelKey={bicLabelKey}
          />
        )}
      </ShowSection>

      <ShowSection
        leftAligned
        label={`resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.rib.title`}
        noCard>
        <FileInput
          source={`${prefix}rib`}
          label={translate(
            `resources.bo_common.b2b.companies.payment_methods.sections.details.${paymentMethodType}.rib.upload`,
            {
              company_name: companyName,
            },
          )}
          accept=".pdf"
          isRequired
          sx={{
            '.RaLabeled-label': {
              ml: 0,
            },
          }}
          validate={[required()]}
          data-testid={`${testId}_RibFileInput`}>
          <FileField source="src" title="title" data-testid={`${testId}_RibFileField`} />
        </FileInput>
      </ShowSection>
    </>
  );
};
